// End-game modal shown when the player completes a puzzle. Mirrors the
// PauseMenu shape — focus-trapped, click-out blocked. Two actions:
// Play again (start a new puzzle at the same difficulty) and Return to
// menu. The scene supplies stats + callbacks; the modal owns no state.

import {
  createCrestButton,
  el,
  installFocusTrap,
  type FocusTrapHandle,
} from '@bn-games/ui-shell';
import { t } from '../i18n/I18n.js';
import type { Difficulty } from '../game/types.js';

export interface EndGameStats {
  readonly difficulty: Difficulty;
  readonly timeLabel: string;
  readonly hintsUsed: number;
  readonly errorsMade: number;
  readonly forfeited: boolean;
}

export interface EndGameModalCallbacks {
  readonly onPlayAgain: () => void;
  readonly onBackToMenu: () => void;
  /** Dismiss the modal without leaving the scene. Forfeit-only: lets the
   *  player study the revealed grid before deciding what to do next. */
  readonly onClose: () => void;
}

let endTitleIdSeq = 0;

export class EndGameModal {
  private overlayNode: HTMLElement | null = null;
  private trap: FocusTrapHandle | null = null;

  constructor(private readonly callbacks: EndGameModalCallbacks) {}

  isOpen(): boolean {
    return this.overlayNode !== null;
  }

  open(stats: EndGameStats): void {
    if (this.isOpen()) return;
    const titleId = `sudoku-end-title-${++endTitleIdSeq}`;

    const overlay = el('div', { class: 'modal-overlay' });
    const card = el('div', {
      class: 'modal-card',
      attrs: {
        role: 'dialog',
        'aria-modal': 'true',
        'aria-labelledby': titleId,
        tabindex: '-1',
      },
    });

    const titleText = stats.forfeited ? t('game.end.forfeitedTitle') : t('game.end.title');
    const subtitleText = stats.forfeited
      ? t('game.end.forfeitedSubtitle')
      : t('game.end.subtitle', { difficulty: difficultyLabel(stats.difficulty) });
    card.appendChild(
      el('h2', { class: 'modal-title', text: titleText, attrs: { id: titleId } }),
    );
    card.appendChild(el('p', { class: 'modal-subtitle', text: subtitleText }));

    const body = el('div', { class: 'modal-body end-game-stats' });
    body.appendChild(statRow(t('game.end.timeLabel'), stats.timeLabel));
    body.appendChild(statRow(t('game.end.hintsLabel'), String(stats.hintsUsed)));
    body.appendChild(statRow(t('game.end.errorsLabel'), String(stats.errorsMade)));
    card.appendChild(body);

    const actions = el('div', { class: 'modal-actions end-game-actions' });
    if (stats.forfeited) {
      // Forfeit path: the player asked to see the answer — give them an
      // explicit "look at the board" exit before they leave the puzzle.
      actions.appendChild(
        createCrestButton({
          label: t('game.end.viewSolution'),
          small: true,
          onClick: () => this.handle(this.callbacks.onClose),
        }),
      );
    }
    actions.appendChild(
      createCrestButton({
        label: t('game.end.backToMenu'),
        small: true,
        onClick: () => this.handle(this.callbacks.onBackToMenu),
      }),
    );
    actions.appendChild(
      createCrestButton({
        label: t('game.end.playAgain'),
        primary: true,
        small: true,
        onClick: () => this.handle(this.callbacks.onPlayAgain),
      }),
    );
    card.appendChild(actions);

    overlay.appendChild(card);
    document.body.appendChild(overlay);
    this.overlayNode = overlay;
    this.trap = installFocusTrap(card);
  }

  close(): void {
    if (this.trap !== null) {
      this.trap.release();
      this.trap = null;
    }
    if (this.overlayNode !== null) {
      this.overlayNode.remove();
      this.overlayNode = null;
    }
  }

  private handle(cb: () => void): void {
    this.close();
    cb();
  }
}

function statRow(label: string, value: string): HTMLElement {
  const row = el('div', { class: 'end-game-stat' });
  row.appendChild(el('span', { class: 'end-game-stat__label', text: label }));
  row.appendChild(el('span', { class: 'end-game-stat__value', text: value }));
  return row;
}

function difficultyLabel(d: Difficulty): string {
  switch (d) {
    case 'easy': return t('play.difficulty.easy');
    case 'medium': return t('play.difficulty.medium');
    case 'hard': return t('play.difficulty.hard');
  }
}
